import { useEffect } from 'react';
import { OAUTH_POPUP_REDIRECT_PARAM } from './agent-integrations.helpers';

/**
 * Reports whether the current window is the OAuth popup that the Google
 * callback redirected back into the app.
 *
 * @returns {boolean} True when the URL carries `oauth_popup=1`
 */
function isOAuthPopupWindow(): boolean {
  return new URLSearchParams(window.location.search).get(OAUTH_POPUP_REDIRECT_PARAM) === '1';
}

/**
 * Closes the window on mount when it is the Google OAuth popup.
 *
 * The Google OAuth callback redirects the popup back to the legacy
 * integrations route (there is no server-rendered self-closing page like
 * Slack/Teams have). The popup marker travels as the `oauth_popup` query param
 * because Chromium clears `window.name` on the cross-site OAuth navigations.
 * The opener polls `popup.closed` and refetches status once it closes.
 *
 * @returns {boolean} True when this window is the popup (and is closing), so callers can render nothing
 */
function useOAuthPopupAutoClose(): boolean {
  const isPopup = isOAuthPopupWindow();

  useEffect(() => {
    if (isPopup) {
      window.close();
    }
  }, [isPopup]);

  return isPopup;
}

export { isOAuthPopupWindow, useOAuthPopupAutoClose };
